import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import TopWedge from "@/components/TopWedge";
import { useAuth } from "@/lib/auth";
import { apiGet } from "@/lib/api";
import { C, metaForSlug } from "@/lib/brand";
import { titleCase } from "@/lib/format";

export default function AppHome() {
  const { consumer } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    apiGet("/consumer/programs", { auth: false }).then(setPrograms).catch(() => {});
    apiGet("/consumer/subscriptions").then(setSubs).catch(() => {});
  }, []);

  const featured = programs[0];
  const meta = featured ? metaForSlug(featured.slug) : null;

  return (
    <div data-testid="app-home" style={{ background: C.cream }}>
      {/* Hero */}
      <section style={{ background: C.ink, color: C.cream, padding: "72px 24px 88px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Eyebrow color={C.peach}>★ HELLO {titleCase(consumer?.firstName || "MEMBER").toUpperCase()}</Eyebrow>
          <h1 style={{ marginTop: 18, fontSize: "clamp(40px, 8vw, 80px)", fontWeight: 800, lineHeight: 0.95, letterSpacing: "-0.04em", textTransform: "uppercase" }}>
            <span style={{ fontFamily: '"Instrument Serif", serif', fontStyle: "italic", fontWeight: 400, textTransform: "none", color: C.peach }}>Today,</span>
            <br />
            FEEL BETTER<br />
            <span style={{ color: C.peach }}>SOONER.</span>
          </h1>
          <p style={{ marginTop: 24, maxWidth: 540, fontSize: 16, color: "rgba(250,246,240,0.7)", lineHeight: 1.55 }}>
            Browse our programs, take the assessment, and get matched with a licensed physician within 24 hours.
          </p>
          <div style={{ marginTop: 32 }}>
            <Link to="/app/programs"><Button variant="peach" size="lg" testId="home-browse-btn">BROWSE PROGRAMS →</Button></Link>
          </div>
        </div>
      </section>

      {subs && subs.length > 0 && (
        <section style={{ padding: "56px 24px 0 24px", position: "relative", background: C.cream }}>
          <TopWedge color={C.ink} dir="right" />
          <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", background: C.peach, position: "relative", zIndex: 2 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDark }}>★ ACTIVE PLAN</div>
              <div style={{ marginTop: 4, fontSize: 14, fontWeight: 700, color: C.ink }}>{subs[0].productName}</div>
            </div>
            <Link to="/app/plan" data-testid="home-plan-link" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.ink }}>VIEW PLAN →</Link>
          </div>
        </section>
      )}

      {/* Featured program */}
      {featured && meta && (
        <section style={{ padding: "76px 24px 64px", position: "relative", background: C.cream }}>
          {!(subs && subs.length > 0) && <TopWedge color={C.ink} dir="right" />}
          <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
            <Eyebrow>★ FEATURED</Eyebrow>
            <Headline before="OUR" italic="bestseller." size={56} className="mt-4" />
            <Link to={`/app/programs/${featured.slug}`} data-testid="home-featured-card">
              <div style={{ marginTop: 32, background: meta.color, padding: "48px 36px", color: meta.dark, position: "relative", overflow: "hidden", cursor: "pointer" }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em" }}>{(featured.tagline || "PROGRAM").toUpperCase()}</div>
                <div style={{ marginTop: 18, fontSize: 44, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 0.95 }}>
                  {(featured.name || "").toUpperCase()}
                </div>
                <div style={{ marginTop: 14, fontFamily: '"Instrument Serif", serif', fontStyle: "italic", fontSize: 22 }}>
                  "{meta.q}"
                </div>
                <div style={{ marginTop: 28, fontSize: 12, fontWeight: 800, letterSpacing: "0.2em" }}>EXPLORE →</div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* How it works */}
      <section style={{ padding: "84px 24px 64px", position: "relative", background: C.cream }}>
        <TopWedge color={C.cream} dir="left" />
        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <Eyebrow>02 / THE PROCESS</Eyebrow>
          <Headline before="HOW IT" italic="actually works." size={56} className="mt-4" />
          <div style={{ marginTop: 40, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0 }} className="!grid-cols-1 md:!grid-cols-3">
            {[
              { n: "01", t: "TAKE THE ASSESSMENT", s: "5 minutes. Real questions a real doctor wants answered.", bg: C.peach, fg: C.peachDark },
              { n: "02", t: "PHYSICIAN REVIEW", s: "A licensed MD reviews your case within 24 hours.", bg: C.sage, fg: C.sageDark },
              { n: "03", t: "PROGRAMS DELIVERED", s: "Discreet monthly shipping. Cancel whenever.", bg: C.lavender, fg: C.lavenderDeep },
            ].map((s) => (
              <div key={s.n} data-testid={`home-step-${s.n}`} style={{ background: s.bg, color: s.fg, padding: "48px 32px 56px" }}>
                <div style={{ fontFamily: '"Instrument Serif", serif', fontStyle: "italic", fontSize: 72, lineHeight: 0.9 }}>{s.n}</div>
                <div style={{ marginTop: 12, fontSize: 14, fontWeight: 800, letterSpacing: "0.18em" }}>{s.t}</div>
                <div style={{ marginTop: 14, fontSize: 14, lineHeight: 1.55 }}>{s.s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: C.ink, color: C.cream, padding: "112px 24px 96px", textAlign: "center", position: "relative" }}>
        <TopWedge color={C.cream} dir="right" />
        <div style={{ maxWidth: 720, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <Eyebrow color={C.peach}>★ READY?</Eyebrow>
          <Headline before="LEVEL" italic="up." color={C.cream} italicColor={C.peach} size={80} className="mt-4" />
          <div style={{ marginTop: 32 }}>
            <Link to="/app/programs"><Button variant="peach" size="lg" testId="home-cta-explore">EXPLORE ALL →</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
